/**
 * Management dashboard overlay — the operator's read on the whole asset.
 *
 * Everything here is derived from the same unit records the map draws, so a
 * number in this panel always reconciles with the unit you can click on.
 */

import React, { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import {
    X, Building2, Percent, Wallet, TrendingUp, Users, AlertTriangle,
    CalendarClock, Trophy, LayoutGrid,
} from 'lucide-react';
import { formatMoney, formatMoneyExact, formatNumber } from '../../Shared/Formatters';
import { buildMallStats, UNITS, FLOORS, CATEGORY_COLORS, formatDateLong } from '../data/mallData';

function Tile({ icon, label, value, sub, tone }) {
    return (
        <div className={`mm-tile${tone ? ` is-${tone}` : ''}`}>
            <span className="mm-tile-icon">{icon}</span>
            <span className="mm-tile-label">{label}</span>
            <strong className="mm-tile-value">{value}</strong>
            {sub && <span className="mm-tile-sub">{sub}</span>}
        </div>
    );
}

export default function OpsDashboard({ theme = 'dark', onClose, onSelectUnit, units = UNITS }) {
    const [scope, setScope] = useState('ALL');

    const stats = useMemo(() => {
        const scoped = scope === 'ALL' ? units : units.filter((u) => u.floor === scope);
        return buildMallStats(scoped);
    }, [scope, units]);

    const dark = theme === 'dark';
    const axisColor = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';

    const trendOptions = useMemo(() => ({
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent' },
        theme: { mode: dark ? 'dark' : 'light' },
        plotOptions: { bar: { columnWidth: '58%', borderRadius: 4 } },
        dataLabels: { enabled: false },
        grid: { borderColor: axisColor, strokeDashArray: 4 },
        xaxis: {
            categories: stats.monthlyTotals.map((m) => m.label),
            axisBorder: { show: false }, axisTicks: { show: false },
            labels: { style: { fontSize: '10px' } },
        },
        yaxis: { labels: { formatter: (v) => formatMoney(v), style: { fontSize: '10px' } } },
        tooltip: { y: { formatter: (v) => formatMoneyExact(v) } },
        colors: ['#3B82F6'],
    }), [stats, dark, axisColor]);

    const catOptions = useMemo(() => ({
        chart: { type: 'donut', fontFamily: 'inherit', background: 'transparent' },
        theme: { mode: dark ? 'dark' : 'light' },
        labels: stats.byCategory.map((c) => c.category),
        colors: stats.byCategory.map((c) => CATEGORY_COLORS[c.category] || '#64748B'),
        legend: { position: 'bottom', fontSize: '11px', markers: { width: 8, height: 8 } },
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        plotOptions: {
            pie: {
                donut: {
                    size: '68%',
                    labels: {
                        show: true,
                        total: { show: true, label: 'Total sales', formatter: () => formatMoney(stats.totalSales) },
                    },
                },
            },
        },
        tooltip: { y: { formatter: (v) => formatMoneyExact(v) } },
    }), [stats, dark]);

    return (
        <div className="mm-dash" role="region" aria-label="Mall management dashboard">
            <header className="mm-dash-head">
                <h3><LayoutGrid size={17} /> Management dashboard</h3>
                <div className="mm-dash-scope">
                    <button type="button" className={scope === 'ALL' ? 'is-active' : ''} onClick={() => setScope('ALL')}>All floors</button>
                    {FLOORS.map((f) => (
                        <button key={f.id} type="button" className={scope === f.id ? 'is-active' : ''} onClick={() => setScope(f.id)}>{f.short}</button>
                    ))}
                </div>
                <button type="button" className="mm-dash-close" onClick={onClose} aria-label="Close dashboard"><X size={18} /></button>
            </header>

            <div className="mm-dash-body">
                <div className="mm-tiles">
                    <Tile icon={<Percent size={16} />} label="Occupancy (by GLA)" value={`${stats.occupancyRate}%`}
                        sub={`${stats.occupiedUnits} occupied · ${stats.vacantUnits} vacant`}
                        tone={stats.occupancyRate < 90 ? 'warn' : 'good'} />
                    <Tile icon={<Building2 size={16} />} label="Units" value={formatNumber(stats.totalUnits)}
                        sub={`${stats.comingSoon} coming soon · ${stats.underRenovation} in fit-out`} />
                    <Tile icon={<LayoutGrid size={16} />} label="Total GLA" value={`${formatNumber(stats.totalGla)} m²`}
                        sub={`${formatNumber(stats.occupiedGla)} m² let`} />
                    <Tile icon={<TrendingUp size={16} />} label="Sales (trailing 12m)" value={formatMoney(stats.totalSales)}
                        sub={`${formatMoneyExact(stats.avgSalesPerSqm)} / m² average`} />
                    <Tile icon={<Wallet size={16} />} label="Contracted base rent" value={formatMoney(stats.totalRent)}
                        sub={`${formatMoneyExact(stats.avgRentPerSqm)} / m² average`} />
                    <Tile icon={<Users size={16} />} label="Footfall (30d)" value={formatNumber(stats.totalFootfall)}
                        sub="sum of unit-level counts" />
                    <Tile icon={<Percent size={16} />} label="Avg occupancy cost" value={`${stats.avgOccupancyCost}%`}
                        sub="rent + CAM ÷ sales" tone={stats.avgOccupancyCost > 20 ? 'warn' : 'good'} />
                    <Tile icon={<AlertTriangle size={16} />} label="High vacancy risk" value={formatNumber(stats.atRisk.length)}
                        sub="units flagged for review" tone={stats.atRisk.length ? 'bad' : 'good'} />
                </div>

                <div className="mm-dash-charts">
                    <div className="mm-dash-card">
                        <h4>Mall sales — trailing 12 months</h4>
                        <Chart options={trendOptions} series={[{ name: 'Net sales', data: stats.monthlyTotals.map((m) => m.revenue) }]} type="bar" height={230} />
                    </div>
                    <div className="mm-dash-card">
                        <h4>Sales mix by category</h4>
                        <Chart options={catOptions} series={stats.byCategory.map((c) => c.sales)} type="donut" height={280} />
                    </div>
                </div>

                <div className="mm-dash-lists">
                    <div className="mm-dash-card">
                        <h4><Trophy size={14} /> Top 10 performers</h4>
                        <ol className="mm-ranklist">
                            {stats.topPerformers.map((u, i) => (
                                <li key={u.id}>
                                    <span className="mm-rank-no">{i + 1}</span>
                                    <button type="button" onClick={() => onSelectUnit(u.id)}>
                                        <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[u.category] }} />
                                        <span className="mm-rank-name">{u.name}</span>
                                        <em>{u.code} · {u.floor}</em>
                                    </button>
                                    <span className="mm-rank-val">{formatMoney(u.performance.last12)}</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className="mm-dash-card">
                        <h4><CalendarClock size={14} /> Leases expiring within 6 months</h4>
                        {stats.expiring6.length ? (
                            <ol className="mm-ranklist">
                                {stats.expiring6.map((u) => (
                                    <li key={u.id}>
                                        <span className="mm-rank-no is-warn">{u.lease.remainingMonths}m</span>
                                        <button type="button" onClick={() => onSelectUnit(u.id)}>
                                            <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[u.category] }} />
                                            <span className="mm-rank-name">{u.name}</span>
                                            <em>{u.code} · expires {formatDateLong(u.lease.endDate)}</em>
                                        </button>
                                        <span className="mm-rank-val">{formatMoney(u.lease.baseRentAnnual)}</span>
                                    </li>
                                ))}
                            </ol>
                        ) : <p className="mm-note">No leases expiring in the next six months.</p>}
                    </div>

                    <div className="mm-dash-card">
                        <h4><AlertTriangle size={14} /> Vacancy risk watchlist</h4>
                        {stats.atRisk.length ? (
                            <ol className="mm-ranklist">
                                {stats.atRisk.slice(0, 10).map((u) => (
                                    <li key={u.id}>
                                        <span className="mm-rank-no is-bad">{u.vacancyRisk}</span>
                                        <button type="button" onClick={() => onSelectUnit(u.id)}>
                                            <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[u.category] }} />
                                            <span className="mm-rank-name">{u.name}</span>
                                            <em>
                                                {u.performance ? `${u.performance.vsCategoryPct}% vs category` : 'no trading data'}
                                                {u.performance ? ` · occ. cost ${u.performance.occupancyCostRatio}%` : ''}
                                            </em>
                                        </button>
                                        <span className="mm-rank-val">{u.riskLabel}</span>
                                    </li>
                                ))}
                            </ol>
                        ) : <p className="mm-note">Nothing on the watchlist for this scope.</p>}
                    </div>

                    <div className="mm-dash-card">
                        <h4><Building2 size={14} /> Category performance</h4>
                        <div className="mm-table-wrap">
                            <table className="mm-table">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th className="num">Units</th>
                                        <th className="num">GLA</th>
                                        <th className="num">Sales 12m</th>
                                        <th className="num">Sales / m²</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.byCategory.map((c) => (
                                        <tr key={c.category}>
                                            <td>
                                                <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[c.category] }} />
                                                {c.category}
                                            </td>
                                            <td className="num">{c.units}</td>
                                            <td className="num">{formatNumber(c.gla)} m²</td>
                                            <td className="num">{formatMoney(c.sales)}</td>
                                            <td className="num">{formatMoneyExact(Math.round(c.sales / c.gla))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
